import { useLingui } from '@lingui/react/macro';
import { useCallback, useState } from 'react';
import { SingleValue, StylesConfig } from 'react-select';

import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useLazyListPlayersQuery } from 'modules/players/store/players-api';
import { getFullName } from 'shared/utils';
import { UDAsyncSelect } from 'ud-form/components/select/component';
import { UDText } from 'ud-ui/components';
import { PlayerSelectOption, playerToPlayerOption } from './player-option';

/** Выдача поиска игроков — длиннее командной, ФИО чаще совпадают. */
const SEARCH_LIMIT = 30;

const selectComponents = {
  // Это поиск, а не список: стрелка «раскрыть» ничего не даёт, игрока ищут вводом ФИО.
  DropdownIndicator: () => null,
  SingleValue: (props: any) => (
    <div>
      <UDText type="subhead">{getFullName(props.data?.value)}</UDText>
    </div>
  ),
};

/** Список ближе к полю: у react-select отступ меню 8px, здесь на 5px меньше. */
const selectStyles: StylesConfig<PlayerSelectOption, false> = {
  menu: (base) => ({ ...base, marginTop: 3 }),
};

interface PlayerAsyncSelectProps {
  value: PlayerSelectOption | null;
  onChange: (option: SingleValue<PlayerSelectOption>) => void;
  autoFocus?: boolean;
  className?: string;
  placeholder?: string;
}

/**
 * Поиск игрока текущей организации по ФИО.
 *
 * Организацию берёт сам — вызывающей стороне остаётся принять выбранного игрока.
 * Используется там, где игрока надо к чему-то привязать: сейчас это добавление в состав
 * команды.
 *
 * Список выдачи открыт, только пока в поле есть хотя бы один символ: клик по пустому полю
 * ничего не раскрывает — искать пока нечего. Ввод держим здесь (`inputValue`), а `menuIsOpen`
 * выводим из него; react-select сам сбрасывает ввод после выбора и при потере фокуса, так что
 * список закрывается и в этих случаях.
 */
export function PlayerAsyncSelect({ value, onChange, autoFocus, className, placeholder }: PlayerAsyncSelectProps) {
  const { t } = useLingui();
  const { info } = useOrganizer();
  const tournamentId = info?.id;
  const [searchPlayers] = useLazyListPlayersQuery();
  const [inputValue, setInputValue] = useState('');

  const handleLoadOptions = useCallback(
    async (query: string) => {
      if (!tournamentId) return [];

      // preferCacheValue: повтор того же ввода между открытиями модалки в сеть не идёт.
      const players = await searchPlayers({ tournamentId, limit: SEARCH_LIMIT, offset: 0, nameFilter: query }, true).unwrap();

      return players.map(playerToPlayerOption);
    },
    [tournamentId, searchPlayers],
  );

  return (
    <UDAsyncSelect<PlayerSelectOption>
      autoFocus={autoFocus}
      className={className}
      placeholder={placeholder ?? t`Введите ФИО игрока`}
      components={selectComponents}
      styles={selectStyles}
      // Список помещается в модалку сам (`MenuList` пресета `UDAsyncSelect`); автопрокрутка к нему двигала бы саму модалку.
      menuShouldScrollIntoView={false}
      inputValue={inputValue}
      onInputChange={setInputValue}
      menuIsOpen={inputValue.length > 0}
      loadOptions={handleLoadOptions}
      value={value}
      onChange={onChange}
    />
  );
}
